"use client";

import { LoginUserType } from "@/types/auth/LoginUserTypes";
import { useQueryClient } from "@tanstack/react-query";
import { signOut, useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { createContext, useContext, useEffect } from "react";
import { usePromoCode } from "./PromoCodeProvider";

interface AuthContextTypes {
  user: LoginUserType | null;
  isAuthenticated: boolean;
  logout: () => void;
  isAuthLoading: boolean;
}

const AuthContext = createContext<AuthContextTypes>({
  user: null,
  isAuthenticated: false,
  logout: () => {},
  isAuthLoading: true,
});

function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();
  const { setPromoCodeData } = usePromoCode();
  const queryClient = useQueryClient();
  const router = useRouter();

  const user = (session?.user as LoginUserType | undefined) ?? null;
  const isAuthenticated = Boolean(session?.accessToken && user?.id);
  const isAuthLoading = status === "loading";

  useEffect(() => {
    if (status === "unauthenticated" || status === "loading") return;

    if (session?.authError === "RefreshAccessTokenTransientError") {
      return;
    }

    if (session?.authError === "RefreshAccessTokenRejectedError") {
      void signOut({ redirect: false });
      queryClient.clear();
      setPromoCodeData(null);
      return;
    }

    if (!session?.accessToken && !session?.portalData?.nonce) {
      void signOut({ redirect: false });
      queryClient.clear();
      setPromoCodeData(null);
    }
  }, [
    status,
    session?.accessToken,
    session?.authError,
    session?.portalData?.nonce,
    queryClient,
    setPromoCodeData,
  ]);

  function logout() {
    queryClient.clear();
    setPromoCodeData(null);
    void signOut({ redirect: false }).then(() => {
      router.refresh();
    });
  }

  const value = {
    user,
    isAuthenticated,
    isAuthLoading,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthProvider;

export function useAuth() {
  return useContext(AuthContext);
}
